import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import _get from 'lodash/get';
import { useApolloClient } from '@apollo/client/react';
import {
  Department,
  DepartmentsSearchListDocument,
  DepartmentsSearchListQuery,
  DepartmentsSearchListQueryVariables,
  FilterGroupingEnum,
  SortDirectionEnum,
} from 'graphql-common';
import PaginatedList, {
  PaginatedListMetadata,
} from '@lib/components/PaginatedList/PaginatedList';

import LinkComponent, { LinkSizes } from '@lib/components/Link/Link';
import { APP_URLS } from 'constants/urls';

type Props = {
  teamId: string;
};

export default function TeamDepartmentsCell({ teamId }: Props) {
  const navigate = useNavigate();
  const client = useApolloClient();
  const fetchDepartments = useCallback(
    (options: { variables: DepartmentsSearchListQueryVariables }) =>
      client.query<
        DepartmentsSearchListQuery,
        DepartmentsSearchListQueryVariables
      >({
        query: DepartmentsSearchListDocument,
        variables: options.variables,
        fetchPolicy: 'no-cache',
      }),
    [client],
  );

  const onView = (department: Department) => {
    if (department?.id) {
      navigate(
        APP_URLS.dashboard.departments.view.getDynamicPath({
          pathParts: { id: department.id },
        }),
      );
    }
  };

  return (
    <PaginatedList<
      Department,
      DepartmentsSearchListQuery,
      DepartmentsSearchListQueryVariables
    >
      query={fetchDepartments}
      variables={{
        sorts: { name: SortDirectionEnum.Asc },
        filters: [
          {
            teamsId: {
              grouping: FilterGroupingEnum.And,
              predicate: { in: [teamId] },
            },
          },
        ],
      }}
      getCollection={(data) =>
        _get(data, 'data.collection', []) as Department[]
      }
      getMetadata={(data) =>
        _get(data, 'data.metadata') as PaginatedListMetadata | undefined
      }
      getItemKey={(item) => item.id}
      renderItem={(item) => (
        <LinkComponent
          size={LinkSizes.small}
          onClick={() => onView(item)}
          data-test-id="team-departments-cell__department"
        >
          {item.name}
        </LinkComponent>
      )}
      useShowMore={false}
      showAllUrl={APP_URLS.dashboard.teams.departments.getDynamicPath({
        pathParts: { id: teamId },
      })}
    />
  );
}
